/**
 * Authentication context — provides auth state and actions throughout the app
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authService, User } from '../services/authService';
import { userService } from '../services/userService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(authService.getToken());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // On mount, validate any existing token by fetching the current user
  useEffect(() => {
    const storedToken = authService.getToken();
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    userService
      .getCurrentUser()
      .then((fetchedUser) => {
        setUser(fetchedUser);
        setToken(storedToken);
      })
      .catch(() => {
        // Token is invalid — clear it
        authService.removeToken();
        setToken(null);
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const authResponse = await authService.login(email, password);
    authService.setToken(authResponse.access_token);
    setToken(authResponse.access_token);
    const fetchedUser = await userService.getCurrentUser();
    setUser(fetchedUser);
  }, []);

  const signup = useCallback(
    async (email: string, password: string, displayName?: string): Promise<void> => {
      const authResponse = await authService.signup(email, password, displayName);
      authService.setToken(authResponse.access_token);
      setToken(authResponse.access_token);
      const fetchedUser = await userService.getCurrentUser();
      setUser(fetchedUser);
    },
    []
  );

  const logout = useCallback((): void => {
    authService.removeToken();
    setToken(null);
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: user !== null && token !== null,
    isLoading,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
